import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, startOfWeek, addDays, isSameDay, parseISO, addWeeks, subWeeks, startOfDay, setHours } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Truck,
  Clock,
  MapPin,
  Package,
  User,
  ArrowRight
} from "lucide-react";
import { Link } from "wouter";

interface ScheduledEvent {
  id: number;
  plateNumber: string;
  driverName: string;
  clientName: string;
  productName: string;
  orderNumber: string;
  scheduledTime: Date;
  actualTime?: Date;
  status: string;
  estimatedDuration: number;
  lane?: string;
}

const TIME_SLOTS = [
  "06:00", "07:00", "08:00", "09:00", "10:00", "11:00",
  "12:00", "13:00", "14:00", "15:00", "16:00", "17:00",
  "18:00", "19:00", "20:00", "21:00"
];

export default function SchedulingCalendar() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState<"week" | "day">("week");

  const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
  const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const { data: kanbanData, isLoading } = useQuery({
    queryKey: ["/api/kanban/trucks2"],
    refetchInterval: 30000,
  });

  const mapKanbanToEvents = (data: any[]): ScheduledEvent[] => {
    if (!data) return [];
    return data.map((truck: any) => {
      let scheduledTime: Date;
      if (truck.scheduledArrival) {
        scheduledTime = parseISO(truck.scheduledArrival);
      } else if (truck.eta) {
        scheduledTime = parseISO(truck.eta);
      } else if (truck.checkInTime) {
        scheduledTime = parseISO(truck.checkInTime);
      } else {
        scheduledTime = new Date();
      }

      return {
        id: truck.id,
        plateNumber: truck.plateNumber || "Unknown",
        driverName: truck.driverName || "Unknown Driver",
        clientName: truck.clientName || "Unknown Client",
        productName: truck.productName || "General",
        orderNumber: truck.orderNumber || `ORD-${truck.id}`,
        scheduledTime,
        actualTime: truck.actualArrival ? parseISO(truck.actualArrival) : undefined,
        status: truck.status || "scheduled",
        estimatedDuration: 30,
        lane: truck.lane
      };
    });
  };

  const events = mapKanbanToEvents(kanbanData as any[]);

  const getEventsForDayAndHour = (day: Date, hour: string) => {
    const hourNum = parseInt(hour.split(":")[0]);
    return events.filter(event => {
      const eventDay = startOfDay(event.scheduledTime);
      const targetDay = startOfDay(day);
      const eventHour = event.scheduledTime.getHours();
      return isSameDay(eventDay, targetDay) && eventHour === hourNum;
    });
  };

  const getEventsForDay = (day: Date) => {
    return events.filter(event => {
      const eventDay = startOfDay(event.scheduledTime);
      const targetDay = startOfDay(day);
      return isSameDay(eventDay, targetDay);
    }).sort((a, b) => a.scheduledTime.getTime() - b.scheduledTime.getTime());
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "completed": return "bg-green-500/20 text-green-400 border-green-500/30";
      case "weighing": 
      case "first_weigh":
      case "second_weigh": return "bg-blue-500/20 text-blue-400 border-blue-500/30";
      case "in_progress":
      case "loading": return "bg-amber-500/20 text-amber-400 border-amber-500/30";
      case "scheduled":
      case "pending": return "bg-purple-500/20 text-purple-400 border-purple-500/30";
      case "checked_in": return "bg-cyan-500/20 text-cyan-400 border-cyan-500/30";
      default: return "bg-gray-500/20 text-gray-400 border-gray-500/30";
    }
  };

  const getStatusBg = (status: string) => {
    switch (status) {
      case "completed": return "bg-green-500/10 border-l-green-500";
      case "weighing": 
      case "first_weigh":
      case "second_weigh": return "bg-blue-500/10 border-l-blue-500";
      case "in_progress":
      case "loading": return "bg-amber-500/10 border-l-amber-500";
      case "scheduled":
      case "pending": return "bg-purple-500/10 border-l-purple-500";
      case "checked_in": return "bg-cyan-500/10 border-l-cyan-500";
      default: return "bg-gray-500/10 border-l-gray-500";
    }
  };

  const navigateWeek = (direction: "prev" | "next") => {
    setCurrentDate(prev => direction === "next" ? addWeeks(prev, 1) : subWeeks(prev, 1));
  };

  const navigateDay = (direction: "prev" | "next") => {
    setCurrentDate(prev => addDays(prev, direction === "next" ? 1 : -1));
  };

  const goToToday = () => setCurrentDate(new Date());

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-[hsl(187,100%,42%)]">Scheduling Calendar</h1>
          <p className="text-muted-foreground">Time-slot view synced with Kanban board</p>
        </div>
        <div className="flex items-center gap-2">
          <Link href="/kanban-board">
            <Button variant="outline" className="border-[hsl(215,28%,25%)]" data-testid="link-kanban">
              <ArrowRight className="w-4 h-4 mr-2" />
              View Kanban
            </Button>
          </Link>
        </div>
      </div>

      <Card className="bg-[hsl(215,28%,10%)] border-[hsl(215,28%,18%)]">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-2">
              <Button
                size="icon"
                variant="outline"
                onClick={() => view === "week" ? navigateWeek("prev") : navigateDay("prev")}
                className="border-[hsl(215,28%,25%)]"
                data-testid="button-prev"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                size="icon"
                variant="outline"
                onClick={() => view === "week" ? navigateWeek("next") : navigateDay("next")}
                className="border-[hsl(215,28%,25%)]"
                data-testid="button-next"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button
                variant="outline"
                onClick={goToToday}
                className="border-[hsl(215,28%,25%)]"
                data-testid="button-today"
              >
                Today
              </Button>
              <h2 className="text-lg font-semibold text-white ml-2">
                {view === "week" 
                  ? `${format(weekStart, "MMM d")} - ${format(addDays(weekStart, 6), "MMM d, yyyy")}`
                  : format(currentDate, "EEEE, MMMM d, yyyy")
                }
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant={view === "week" ? "default" : "outline"}
                onClick={() => setView("week")}
                className={view === "week" ? "bg-[hsl(24.6,95%,53%)] text-black" : "border-[hsl(215,28%,25%)]"}
                data-testid="button-week-view"
              >
                Week
              </Button>
              <Button
                variant={view === "day" ? "default" : "outline"}
                onClick={() => setView("day")}
                className={view === "day" ? "bg-[hsl(24.6,95%,53%)] text-black" : "border-[hsl(215,28%,25%)]"}
                data-testid="button-day-view"
              >
                Day
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center h-96">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[hsl(187,100%,42%)]" />
            </div>
          ) : view === "week" ? (
            <div className="overflow-x-auto">
              <div className="min-w-[900px]">
                <div className="grid grid-cols-8 border-b border-[hsl(215,28%,18%)]">
                  <div className="p-3 text-xs text-muted-foreground font-medium border-r border-[hsl(215,28%,18%)]">
                    Time
                  </div>
                  {weekDays.map((day, i) => (
                    <div 
                      key={i} 
                      className={`p-3 text-center border-r border-[hsl(215,28%,18%)] last:border-r-0 ${
                        isSameDay(day, new Date()) ? "bg-[hsl(187,100%,42%)]/10" : ""
                      }`}
                    >
                      <p className="text-xs text-muted-foreground">{format(day, "EEE")}</p>
                      <p className={`text-lg font-bold ${isSameDay(day, new Date()) ? "text-[hsl(187,100%,42%)]" : "text-white"}`}>
                        {format(day, "d")}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {getEventsForDay(day).length} visits
                      </p>
                    </div>
                  ))}
                </div>
                <ScrollArea className="h-[600px]">
                  {TIME_SLOTS.map((time) => (
                    <div key={time} className="grid grid-cols-8 border-b border-[hsl(215,28%,18%)] min-h-[60px]">
                      <div className="p-2 text-xs text-muted-foreground border-r border-[hsl(215,28%,18%)] flex items-start">
                        {time}
                      </div>
                      {weekDays.map((day, dayIndex) => {
                        const dayEvents = getEventsForDayAndHour(day, time);
                        return (
                          <div 
                            key={dayIndex} 
                            className={`p-1 border-r border-[hsl(215,28%,18%)] last:border-r-0 ${
                              isSameDay(day, new Date()) ? "bg-[hsl(187,100%,42%)]/5" : ""
                            }`}
                          >
                            {dayEvents.map((event) => (
                              <div
                                key={event.id}
                                className={`p-1.5 rounded text-xs mb-1 border-l-2 ${getStatusBg(event.status)}`}
                                title={`${event.plateNumber} - ${event.clientName}`}
                              >
                                <div className="font-semibold text-white truncate">{event.plateNumber}</div>
                                <div className="text-muted-foreground truncate">{event.clientName}</div>
                              </div>
                            ))}
                          </div>
                        );
                      })}
                    </div>
                  ))}
                </ScrollArea>
              </div>
            </div>
          ) : (
            <div className="p-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-4">Time Slots</h3>
                  <ScrollArea className="h-[600px] pr-4">
                    {TIME_SLOTS.map((time) => {
                      const hourEvents = getEventsForDayAndHour(currentDate, time);
                      return (
                        <div key={time} className="flex gap-4 mb-2">
                          <div className="w-16 text-sm text-muted-foreground pt-2 flex-shrink-0">
                            {time}
                          </div>
                          <div className="flex-1 min-h-[60px] bg-[hsl(215,28%,14%)] rounded-lg p-2 border border-[hsl(215,28%,20%)]">
                            {hourEvents.length > 0 ? (
                              <div className="space-y-2">
                                {hourEvents.map((event) => (
                                  <div
                                    key={event.id}
                                    className={`p-3 rounded-lg border-l-4 ${getStatusBg(event.status)}`}
                                  >
                                    <div className="flex items-center justify-between gap-2 mb-2">
                                      <div className="flex items-center gap-2">
                                        <Truck className="w-4 h-4 text-[hsl(24.6,95%,53%)]" />
                                        <span className="font-semibold text-white">{event.plateNumber}</span>
                                      </div>
                                      <Badge variant="outline" className={getStatusColor(event.status)}>
                                        {event.status.replace("_", " ")}
                                      </Badge>
                                    </div>
                                    <div className="grid grid-cols-2 gap-2 text-xs">
                                      <div className="flex items-center gap-1 text-muted-foreground">
                                        <User className="w-3 h-3" />
                                        {event.driverName}
                                      </div>
                                      <div className="flex items-center gap-1 text-muted-foreground">
                                        <Package className="w-3 h-3" />
                                        {event.productName}
                                      </div>
                                      <div className="flex items-center gap-1 text-muted-foreground">
                                        <MapPin className="w-3 h-3" />
                                        {event.clientName}
                                      </div>
                                      <div className="flex items-center gap-1 text-muted-foreground">
                                        <Clock className="w-3 h-3" />
                                        {format(event.scheduledTime, "HH:mm")}
                                      </div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <div className="flex items-center justify-center h-full text-xs text-muted-foreground">
                                No scheduled visits
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </ScrollArea>
                </div>

                <div>
                  <h3 className="text-sm font-medium text-muted-foreground mb-4">
                    All Visits for {format(currentDate, "MMMM d")}
                  </h3>
                  <ScrollArea className="h-[600px] pr-4">
                    <div className="space-y-3">
                      {getEventsForDay(currentDate).length > 0 ? (
                        getEventsForDay(currentDate).map((event) => (
                          <Card key={event.id} className={`bg-[hsl(215,28%,14%)] border-[hsl(215,28%,20%)] border-l-4 ${getStatusBg(event.status).split(" ")[1]}`}>
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between gap-2 mb-3">
                                <div className="flex items-center gap-2">
                                  <div className="w-10 h-10 bg-[hsl(24.6,95%,53%)]/20 rounded-lg flex items-center justify-center">
                                    <Truck className="w-5 h-5 text-[hsl(24.6,95%,53%)]" />
                                  </div>
                                  <div>
                                    <p className="font-semibold text-white">{event.plateNumber}</p>
                                    <p className="text-xs text-muted-foreground">{event.orderNumber}</p>
                                  </div>
                                </div>
                                <Badge variant="outline" className={getStatusColor(event.status)}>
                                  {event.status.replace("_", " ")}
                                </Badge>
                              </div>
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                <div>
                                  <p className="text-xs text-muted-foreground">Driver</p>
                                  <p className="text-white">{event.driverName}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Client</p>
                                  <p className="text-white">{event.clientName}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Product</p>
                                  <p className="text-white">{event.productName}</p>
                                </div>
                                <div>
                                  <p className="text-xs text-muted-foreground">Scheduled</p>
                                  <p className="text-white">{format(event.scheduledTime, "HH:mm")}</p>
                                </div>
                              </div>
                              {event.actualTime && (
                                <div className="mt-2 pt-2 border-t border-[hsl(215,28%,25%)]">
                                  <p className="text-xs text-muted-foreground">Actual Arrival</p>
                                  <p className="text-green-400">{format(event.actualTime, "HH:mm")}</p>
                                </div>
                              )}
                            </CardContent>
                          </Card>
                        ))
                      ) : (
                        <div className="text-center py-12 text-muted-foreground">
                          <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
                          <p>No visits scheduled for this day</p>
                        </div>
                      )}
                    </div>
                  </ScrollArea>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="flex items-center gap-2 p-3 bg-[hsl(215,28%,12%)] rounded-lg border border-[hsl(215,28%,18%)]">
          <div className="w-3 h-3 rounded-full bg-purple-500" />
          <span className="text-sm text-muted-foreground">Scheduled</span>
        </div>
        <div className="flex items-center gap-2 p-3 bg-[hsl(215,28%,12%)] rounded-lg border border-[hsl(215,28%,18%)]">
          <div className="w-3 h-3 rounded-full bg-cyan-500" />
          <span className="text-sm text-muted-foreground">Checked In</span>
        </div>
        <div className="flex items-center gap-2 p-3 bg-[hsl(215,28%,12%)] rounded-lg border border-[hsl(215,28%,18%)]">
          <div className="w-3 h-3 rounded-full bg-amber-500" />
          <span className="text-sm text-muted-foreground">In Progress</span>
        </div>
        <div className="flex items-center gap-2 p-3 bg-[hsl(215,28%,12%)] rounded-lg border border-[hsl(215,28%,18%)]">
          <div className="w-3 h-3 rounded-full bg-blue-500" />
          <span className="text-sm text-muted-foreground">Weighing</span>
        </div>
        <div className="flex items-center gap-2 p-3 bg-[hsl(215,28%,12%)] rounded-lg border border-[hsl(215,28%,18%)]">
          <div className="w-3 h-3 rounded-full bg-green-500" />
          <span className="text-sm text-muted-foreground">Completed</span>
        </div>
      </div>
    </div>
  );
}
